import React from 'react';
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import Checkbox from "Components/DesignSystem/Checkbox/Checkbox";
import { ResetIcon } from "assests/GmsIcons";
import { Draggable, DraggableChildrenFn, OnDragEndResponder } from "@hello-pangea/dnd";
import { TTableHeader } from "types/Components/Common/Table/Table";
import { useState } from "react";
import DragAndDropContainer from "Components/Common/components/DragAndDrop/DragAndDropContainer";
import DragAndDropHeaderItem from "./DragAndDropHeaderItem";
import * as types from "types/Components/Common/Table/TableHeaderControlModal";

/**
 * Modal to manage headers of table organize and hide/display header
 * columns. All changes are persisted locally, only on save will
 * save the changes globally.
 *
 * On the DS shell since 2026-09-07: `Dialog` (portals to document.body — the
 * legacy #modal-root requirement is gone) + DS `Button`/`Checkbox`; the dnd
 * plumbing (DragAndDropContainer/Draggable, the `element.hidden` space-y hack)
 * and every handler are byte-identical. `closeOnOutsideClick` is passed
 * deliberately: ModalContainer's `clickOutSideClose` defaulted TRUE, so
 * scrim-dismiss is behaviour this modal's users already had (the PDW-migration
 * precedent). Escape and the X are the DS Dialog's additive close paths — both
 * land on the same setHeaderControlOpen(false) Cancel uses. The legacy wrapper
 * id `table-header-control-modal` rides on as the Dialog's data-testid.
 * Reset-to-default sits in the Dialog's `headerActions` slot, beside the X —
 * its legacy position — rather than in a body row that pushed the panels down.
 *
 * @param props.headers - current header properties
 * @param props.defaultHeaders  - default headers to reset
 * @param props.setHeaderControlOpen - function to toggle on/off modal state(for closing)
 * @param props.handleSave - save function new headers passed as param
 * @param props.headerControlOpen - state of modal on/off
 * @returns 
 */
const TableHeaderControlModal: types.TTableHeaderControlModal = ({
    headers, 
    defaultHeaders, 
    setHeaderControlOpen, 
    handleSave, 
    headerControlOpen, 
}) => {
    const [_headers, _setHeaders] = useState<TTableHeader[]>(JSON.parse(JSON.stringify(headers)))
    const [selected, setSelected] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    let draggableComponent: (className?:string) => DraggableChildrenFn;

    // set display to false on all headers
    const handleRemoveAll: types.ThandleRemoveAll  = (e) => {
        e.preventDefault()

        _setHeaders((prevHeaders) => {
            let nextHeaders = [...prevHeaders]

            nextHeaders.map((header) => {
                header.display = false
            })
            return nextHeaders
        })
    }

    // handle checkbox changes
    const handleSelect: types.ThandleSelect = (e, id) => {
        if(e.target.checked) return setSelected(prevSelected => ([...prevSelected, id]))
        setSelected(prevSelected => {
            let nextSelected = [...prevSelected]

            const index = nextSelected.indexOf(id)
            nextSelected.splice(index, 1)

            return nextSelected
        })
    }

    // set display to true for selected headers
    const handleAdd: types.ThandleAdd = (e) => {
        _setHeaders((prevHeaders) => {
            let nextHeaders = [...prevHeaders]

            nextHeaders.map((header, index) => {
                if(selected.includes(header.id as number) || selectAll) {
                    // this is to ensure space-y does not effect hidden elements
                    const element = document.querySelector(`div[data-rfd-draggable-id="columnArrange${index}"]`) as HTMLDivElement
                    if(element) element.hidden = false
                    header.display = true
                }
            })
            return nextHeaders
        })

        setSelected([])
        setSelectAll(false)
    }

    // set display to false index header
    const handleRemove: types.ThandleRemove = (e, index) => {
        e.preventDefault()

        const element = document.querySelector(`div[data-rfd-draggable-id="columnArrange${index}"]`) as HTMLDivElement
        // hidden elements doesn't get effected with space-y
        if(element) element.hidden = true

        _setHeaders(prevHeaders => {
            let nextHeaders = [...prevHeaders]
            nextHeaders[index].display = false
            return nextHeaders
        })
    }

    // toggle select all button
    const handleSelectAll: types.ThandleSelectAll = (e) => {
        e.preventDefault()
        setSelectAll((prev) => !prev)
    }

    // reset headers to default headers
    const handleResetDefault: types.ThandleResetDefault = (e) => {
        e.preventDefault()
        _setHeaders(JSON.parse(JSON.stringify(defaultHeaders.current)))
    }

    /**
     * Change order of the headers on drag and drop
     */
    const onDragEnd: OnDragEndResponder = ({source, destination}) => {
        if(source.index == null || destination?.index == null) return
        _setHeaders((prevHeaders) => {
            let temp = [...prevHeaders]
            const removedHeader = temp.splice(source.index, 1)[0]
            if(!removedHeader) return prevHeaders
            const nextHeaders = [
                ...temp.slice(0, destination.index),
                removedHeader,
                ...temp.slice(destination.index)
            ]
            return nextHeaders
        })
    }

    // save changes and close modal
    const handleUpdate:types.ThandleUpdate = (e) =>{
        e.preventDefault()
        handleSave(_headers)
        setHeaderControlOpen(false)
    }

    draggableComponent = DragAndDropHeaderItem(_headers, handleRemove)

    return(
    <Dialog
        isOpen={headerControlOpen}
        onClose={() => setHeaderControlOpen(false)}
        title="Show/Hide Column"
        size="lg"
        closeOnOutsideClick
        data-testid="table-header-control-modal"
        headerActions={
            <Button variant="secondary" size="sm" data-testid="table-header-control-modal-reset"
                showIcon icon={<ResetIcon/>} onClick={handleResetDefault}>
                Reset to default
            </Button>
        }
        footer={
            <div className={`tw-flex tw-justify-end tw-gap-3`}>
                <Button variant="secondary" data-testid="table-header-control-modal-cancel" onClick={() => setHeaderControlOpen(false)}>
                    Cancel
                </Button>
                <Button data-testid="table-header-control-modal-update" onClick={handleUpdate}>
                    Update
                </Button>
            </div>
        }
    >
        <div className={`tw-flex tw-justify-between tw-space-x-7`}>
            {/* left */}
            <div className={`tw-w-[330px]`}>
                <div className={`tw-flex tw-justify-between tw-items-center`}>
                    <span className={`tw-font-bold tw-text-ink-tertiary`}>Available</span>
                    <Button variant="subtle" size="sm" data-testid="table-header-control-modal-select-all" onClick={handleSelectAll}>
                        Select All
                    </Button>
                </div>

                <div className={`tw-w-full tw-border tw-border-edge tw-rounded-sm tw-h-[319px] tw-min-w-max tw-p-2 tw-space-y-2 tw-overflow-auto`}>
                    {
                        _headers.filter((header) => !header.display).map((header, key) => (
                            <div key = {key} className={`tw-p-1.5 tw-px-2.5`}>
                                <Checkbox
                                    data-testid={`components-tableheadercontrolmodal-input-1-${key}`}
                                    size="sm"
                                    label={header.name}
                                    checked={(selected?.includes(header.id as number) || selectAll)}
                                    onChange={(_checkedNow, e) => { if (e) handleSelect(e, header.id as number); }}
                                />
                            </div>
                        ))
                    }
                </div>
            </div>
            {/* center */}
            <div className={`tw-flex tw-items-center tw-w-min`}>
                <Button variant="secondary" data-testid="table-header-control-modal-add" onClick={handleAdd}>
                    Add
                </Button>
            </div>

            {/* right - drag and drop contents */}
            <div className={`tw-w-[330px] tw-relative`}>
                <div className={`tw-flex tw-justify-between tw-items-center`}>
                    <span className={`tw-font-bold tw-text-ink-tertiary`}>Selected</span>
                    <Button variant="subtle" size="sm" data-testid="table-header-control-modal-remove-all" onClick={handleRemoveAll}>
                        Remove All
                    </Button>
                </div>

                <div className={`tw-max-w-full`}>
                    <DragAndDropContainer id = "table-header-dnd" onDragEnd = {onDragEnd}  renderItem = {draggableComponent("")}>
                        {
                            _headers.map((header, key) =>  (
                            <Draggable key={"columnArrange" + key} index={key} draggableId={"columnArrange" + key}>
                                {
                                    (provided, snapshot, rubric) => {
                                        return draggableComponent(!header.display ? "tw-hidden" : undefined)(provided, snapshot, rubric)
                                    }
                                }
                            </Draggable>
                        ))}
                    </DragAndDropContainer>
                </div>
            </div>
        </div>
    </Dialog>
    )
}

export default TableHeaderControlModal
